import React, { useState, useRef, useEffect } from 'react';
import './App.css';
import './assets/fonts/Regular.woff2';
import './assets/fonts/DemiBold.woff2';

function App() {
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [showCopiedMessage, setShowCopiedMessage] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const canvasRef = useRef(null);
  const backgroundRef = useRef(null);
  const logoRef = useRef(null);
  const [logoAspectRatio, setLogoAspectRatio] = useState(1); // Default to square
  const [canvasSize, setCanvasSize] = useState(0); // Dynamic canvas size

  // URL for the logo image
  const LOGO_URL = "/assets/images/ikzieje.png";

  // Constants for export dimensions and colors
  const EXPORT_SIZE = 1080;
  const LOGO_PERCENTAGE = 0.8; // 80% of canvas width/height (maximum size)
  const CANVAS_COLOR = "#333333"; // Dark grey color

  // Calculate scale factor dynamically
  const getScaleFactor = () => canvasSize ? EXPORT_SIZE / canvasSize : 2;

  // Recalculate canvas size on window resize
  useEffect(() => {
    const updateCanvasSize = () => {
      if (canvasRef.current) {
        const newSize = Math.min(
          canvasRef.current.parentElement.clientWidth,
          window.innerHeight * 0.8 // Limit height to 80% of viewport height
        );
        setCanvasSize(newSize);
      }
    };

    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    return () => window.removeEventListener('resize', updateCanvasSize);
  }, []);

  // Preload the logo image and get its aspect ratio
  useEffect(() => {
    const logoImg = new Image();
    logoImg.onload = () => {
      setLogoAspectRatio(logoImg.naturalWidth / logoImg.naturalHeight);
    };
    logoImg.onerror = () => {
      console.error('Error loading logo from URL:', LOGO_URL);
    };
    logoImg.src = LOGO_URL;
  }, []);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type.match('image.*')) {
      const reader = new FileReader();
      reader.onload = (e) => setBackgroundImage(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleMouseDown = (e) => {
    if (!backgroundImage) return;

    // Get the canvas bounds to calculate the correct position
    const canvasBounds = canvasRef.current.getBoundingClientRect();
    const clickX = e.clientX - canvasBounds.left;
    const clickY = e.clientY - canvasBounds.top;

    setIsDragging(true);
    setDragStart({
      x: clickX - position.x,
      y: clickY - position.y
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    // Get the canvas bounds to calculate the correct position
    const canvasBounds = canvasRef.current.getBoundingClientRect();
    const moveX = e.clientX - canvasBounds.left;
    const moveY = e.clientY - canvasBounds.top;

    setPosition({
      x: moveX - dragStart.x,
      y: moveY - dragStart.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch event handlers
  const handleTouchStart = (e) => {
    if (!backgroundImage) return;

    // Prevent default to avoid scrolling while dragging
    e.preventDefault();

    const touch = e.touches[0];
    const canvasBounds = canvasRef.current.getBoundingClientRect();
    const touchX = touch.clientX - canvasBounds.left;
    const touchY = touch.clientY - canvasBounds.top;

    setIsDragging(true);
    setDragStart({
      x: touchX - position.x,
      y: touchY - position.y
    });
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;

    // Prevent default to avoid scrolling while dragging
    e.preventDefault();

    const touch = e.touches[0];
    const canvasBounds = canvasRef.current.getBoundingClientRect();
    const touchX = touch.clientX - canvasBounds.left;
    const touchY = touch.clientY - canvasBounds.top;

    setPosition({
      x: touchX - dragStart.x,
      y: touchY - dragStart.y
    });
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    // Mouse events
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('mousemove', handleMouseMove);

    // Touch events
    window.addEventListener('touchend', handleTouchEnd);
    window.addEventListener('touchcancel', handleTouchEnd);
    window.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      // Remove mouse events
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('mousemove', handleMouseMove);

      // Remove touch events
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('touchcancel', handleTouchEnd);
      window.removeEventListener('touchmove', handleTouchMove);
    };
  }, [isDragging, dragStart]);

  // Helper function to detect iOS
  const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
           (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  };

  // Helper function to detect if running in iframe
  const isInIframe = () => {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  };

  // Enhanced download function with better Safari iOS support
  const downloadImage = (canvas, filename = 'logo-composition.png') => {
    console.log('Download initiated for:', filename);
    console.log('iOS detected:', isIOS());
    console.log('In iframe:', isInIframe());
    
    try {
      // For Safari iOS, use a more direct approach
      if (isIOS()) {
        console.log('Using iOS-specific download method');
        
        // Method 1: Try data URL approach first for iOS
        try {
          const dataURL = canvas.toDataURL('image/png');
          console.log('Canvas converted to data URL successfully');
          
          // Create a temporary link element
          const link = document.createElement('a');
          link.href = dataURL;
          link.download = filename;
          link.style.display = 'none';
          
          // Add to DOM and trigger click
          document.body.appendChild(link);
          
          // For iOS Safari, we need to trigger the click in a user gesture context
          // and provide immediate feedback
          setTimeout(() => {
            try {
              link.click();
              console.log('Download link clicked');
            } catch (clickErr) {
              console.error('Click failed:', clickErr);
              // Fallback: Open in new tab with instructions
              openImageInNewTab(dataURL, filename);
            }
            document.body.removeChild(link);
          }, 10);
          
        } catch (dataUrlErr) {
          console.error('Data URL method failed:', dataUrlErr);
          alert('Unable to generate image for download. Please try again.');
        }
        
      } else {
        // For non-iOS browsers, use blob method
        console.log('Using blob method for non-iOS browser');
        
        canvas.toBlob((blob) => {
          if (blob) {
            const url = URL.createObjectURL(blob);
            console.log('Blob created successfully');
            
            if (isInIframe()) {
              // For iframe contexts, open in new window
              openImageInNewTab(url, filename, true);
            } else {
              // Standard download for desktop browsers
              const link = document.createElement('a');
              link.href = url;
              link.download = filename;
              
              // Ensure the link is added to DOM for Firefox compatibility
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              
              // Clean up the blob URL
              setTimeout(() => URL.revokeObjectURL(url), 100);
            }
          } else {
            throw new Error('Failed to create blob from canvas');
          }
        }, 'image/png', 0.95);
      }
      
    } catch (err) {
      console.error('Download failed:', err);
      alert('Unable to download the image. This may be due to browser security restrictions. Please try using a different browser or device.');
    }
  };

  // Helper function to open image in new tab with download instructions
  const openImageInNewTab = (imageUrl, filename, isBlob = false) => {
    console.log('Opening image in new tab');
    
    try {
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        const isSafariIOS = isIOS() && /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
        
        newWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>Download ${filename}</title>
              <meta name="viewport" content="width=device-width, initial-scale=1.0">
              <style>
                body {
                  margin: 0;
                  padding: 20px;
                  text-align: center;
                  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
                  background: #f5f5f5;
                }
                .container {
                  max-width: 600px;
                  margin: 0 auto;
                  background: white;
                  padding: 20px;
                  border-radius: 10px;
                  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                }
                h2 { color: #333; margin-bottom: 20px; }
                .image-container {
                  margin: 20px 0;
                  border: 2px solid #ddd;
                  border-radius: 8px;
                  overflow: hidden;
                  display: inline-block;
                }
                img {
                  max-width: 100%;
                  height: auto;
                  display: block;
                }
                .instructions {
                  background: #e8f4fd;
                  padding: 15px;
                  border-radius: 8px;
                  margin: 20px 0;
                  text-align: left;
                }
                .download-btn {
                  display: inline-block;
                  padding: 12px 24px;
                  background: #E52F2F;
                  color: white;
                  text-decoration: none;
                  border-radius: 6px;
                  margin: 10px;
                  font-weight: bold;
                }
                .download-btn:hover {
                  background: #d42626;
                }
                .ios-specific {
                  background: #fff3cd;
                  border: 1px solid #ffeaa7;
                  padding: 15px;
                  border-radius: 8px;
                  margin: 20px 0;
                }
              </style>
            </head>
            <body>
              <div class="container">
                <h2>Your Image is Ready!</h2>
                
                <div class="image-container">
                  <img src="${imageUrl}" alt="Generated Image" id="downloadImage"/>
                </div>
                
                ${isSafariIOS ? `
                  <div class="ios-specific">
                    <strong>📱 For iOS Safari users:</strong><br>
                    1. Tap and hold the image above<br>
                    2. Select "Save to Photos" or "Add to Photos"<br>
                    3. The image will be saved to your Photos app
                  </div>
                ` : `
                  <div class="instructions">
                    <strong>💾 To save this image:</strong><br>
                    • <strong>Desktop:</strong> Right-click the image and select "Save image as..."<br>
                    • <strong>Mobile:</strong> Tap and hold the image, then select "Save image" or "Add to Photos"<br>
                    • <strong>Alternative:</strong> Use the download button below
                  </div>
                `}
                
                <a href="${imageUrl}" download="${filename}" class="download-btn">
                  📥 Download Image
                </a>
                
                <br><br>
                <button onclick="window.close()" style="padding: 8px 16px; background: #6c757d; color: white; border: none; border-radius: 4px; cursor: pointer;">
                  Close Window
                </button>
              </div>
              
              <script>
                // Additional iOS-specific handling
                if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
                  console.log('iOS device detected in download window');
                  
                  // For iOS, also try to trigger download programmatically
                  document.addEventListener('DOMContentLoaded', function() {
                    const img = document.getElementById('downloadImage');
                    if (img) {
                      img.addEventListener('load', function() {
                        console.log('Image loaded in download window');
                      });
                    }
                  });
                }
                
                // Clean up blob URL if needed
                ${isBlob ? `
                  window.addEventListener('beforeunload', function() {
                    if ('${imageUrl}'.startsWith('blob:')) {
                      URL.revokeObjectURL('${imageUrl}');
                    }
                  });
                ` : ''}
              </script>
            </body>
          </html>
        `);
        newWindow.document.close();
        console.log('New window opened with download instructions');
      } else {
        console.error('Failed to open new window - popup blocked');
        // Fallback: try to navigate to the image directly
        // eslint-disable-next-line no-restricted-globals
        if (confirm('Popup was blocked. Would you like to open the image in the current tab?')) {
          window.location.href = imageUrl;
        }
      }
    } catch (newTabErr) {
      console.error('Failed to open new tab:', newTabErr);
      alert('Unable to open download window. Please check your popup blocker settings.');
    }
  };

  const handleDownload = () => {
    setIsDownloading(true);
    
    try {
      // Create a new canvas for exporting
      const canvas = document.createElement('canvas');
      canvas.width = EXPORT_SIZE;
      canvas.height = EXPORT_SIZE;
      const ctx = canvas.getContext('2d');

      // Fill canvas with dark grey background
      ctx.fillStyle = CANVAS_COLOR;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw background image if exists
      if (backgroundImage && backgroundRef.current) {
        const img = backgroundRef.current;
        const SCALE_FACTOR = getScaleFactor();

        // Scale the position and dimensions by the scale factor
        const scaledX = position.x * SCALE_FACTOR;
        const scaledY = position.y * SCALE_FACTOR;
        const scaledWidth = img.naturalWidth * scale * SCALE_FACTOR;
        const scaledHeight = img.naturalHeight * scale * SCALE_FACTOR;

        ctx.drawImage(
          img,
          scaledX, scaledY,
          scaledWidth, scaledHeight
        );
      }

      // Function to draw logo and complete download
      const drawLogoAndDownload = () => {
        const logoImg = new Image();
        
        // Handle CORS more robustly
        logoImg.crossOrigin = "anonymous";
        
        logoImg.onload = () => {
          try {
            // Calculate logo size while maintaining aspect ratio
            const maxLogoSize = EXPORT_SIZE * LOGO_PERCENTAGE;
            let logoWidth, logoHeight;

            if (logoImg.naturalWidth >= logoImg.naturalHeight) {
              // Wider or square logo
              logoWidth = maxLogoSize;
              logoHeight = maxLogoSize / (logoImg.naturalWidth / logoImg.naturalHeight);
            } else {
              // Taller logo
              logoHeight = maxLogoSize;
              logoWidth = maxLogoSize * (logoImg.naturalWidth / logoImg.naturalHeight);
            }

            // Center the logo
            const logoX = (EXPORT_SIZE - logoWidth) / 2;
            const logoY = (EXPORT_SIZE - logoHeight) / 2;

            // Draw the logo at the correct position and size
            ctx.drawImage(logoImg, logoX, logoY, logoWidth, logoHeight);

            // Use the robust download function
            downloadImage(canvas, 'logo-composition.png');
            
            // Reset downloading state after a delay
            setTimeout(() => setIsDownloading(false), 2000);
            
          } catch (drawErr) {
            console.error('Error drawing logo:', drawErr);
            // Try to download without logo as fallback
            downloadImage(canvas, 'logo-composition.png');
            setTimeout(() => setIsDownloading(false), 2000);
          }
        };

        logoImg.onerror = () => {
          console.error('Error loading logo for export, trying without CORS');
          
          // Try loading without CORS as fallback
          const logoImgNoCors = new Image();
          logoImgNoCors.onload = () => {
            try {
              // Calculate logo size while maintaining aspect ratio
              const maxLogoSize = EXPORT_SIZE * LOGO_PERCENTAGE;
              let logoWidth, logoHeight;

              if (logoImgNoCors.naturalWidth >= logoImgNoCors.naturalHeight) {
                logoWidth = maxLogoSize;
                logoHeight = maxLogoSize / (logoImgNoCors.naturalWidth / logoImgNoCors.naturalHeight);
              } else {
                logoHeight = maxLogoSize;
                logoWidth = maxLogoSize * (logoImgNoCors.naturalWidth / logoImgNoCors.naturalHeight);
              }

              const logoX = (EXPORT_SIZE - logoWidth) / 2;
              const logoY = (EXPORT_SIZE - logoHeight) / 2;

              ctx.drawImage(logoImgNoCors, logoX, logoY, logoWidth, logoHeight);
              downloadImage(canvas, 'logo-composition.png');
              setTimeout(() => setIsDownloading(false), 2000);
            } catch (noCorsErr) {
              console.error('Error with no-CORS fallback:', noCorsErr);
              // Download without logo as final fallback
              downloadImage(canvas, 'logo-composition.png');
              setTimeout(() => setIsDownloading(false), 2000);
            }
          };
          
          logoImgNoCors.onerror = () => {
            console.error('All logo loading methods failed, downloading without logo');
            // Download without logo as final fallback
            downloadImage(canvas, 'logo-composition.png');
            setTimeout(() => setIsDownloading(false), 2000);
          };
          
          logoImgNoCors.src = LOGO_URL;
        };

        // Load the logo image with CORS
        logoImg.src = LOGO_URL;
      };

      // Start the logo drawing process
      drawLogoAndDownload();

    } catch (err) {
      console.error('Error in download process:', err);
      setIsDownloading(false);
      alert('An error occurred while trying to download the image.');
    }
  };

  const handleCopyEmbed = () => {
    const embedCode = `<iframe 
  src="https://113-face.vercel.app/" 
  style="width: 100%; height: 700px; border: none; overflow: hidden;"
  scrolling="no" 
  frameborder="0" 
  allowtransparency="true"
  allow="downloads"
></iframe>`;

    navigator.clipboard.writeText(embedCode)
      .then(() => {
        setShowCopiedMessage(true);
        setTimeout(() => setShowCopiedMessage(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
        // Fallback for browsers that don't support clipboard API
        alert('Could not copy to clipboard automatically. Please copy this code manually:\n\n' + embedCode);
      });
  };

  // Calculate logo dimensions for display
  const getLogoDisplayDimensions = () => {
    const maxSize = canvasSize * LOGO_PERCENTAGE;
    let width, height;

    if (logoAspectRatio >= 1) {
      // Wider or square logo
      width = maxSize;
      height = maxSize / logoAspectRatio;
    } else {
      // Taller logo
      height = maxSize;
      width = maxSize * logoAspectRatio;
    }

    return { width, height };
  };

  const logoDimensions = getLogoDisplayDimensions();
  const counterStyle = {
    cursor: isDragging ? 'grabbing' : backgroundImage ? 'grab' : 'default'
  };

  return (
    <div className="app-wrapper">

      <div className="controls">
        <div className="buttons">
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="file-input"
            id="background-upload"
          />
          <label
            htmlFor="background-upload"
            className="button"
          >
            Upload foto
          </label>

          {backgroundImage && (
            <button
              onClick={handleDownload}
              className="button"
              disabled={isDownloading}
              style={{
                opacity: isDownloading ? 0.7 : 1,
                cursor: isDownloading ? 'not-allowed' : 'pointer'
              }}
            >
              {isDownloading ? 'Downloading...' : 'Download'}
            </button>
          )}
        </div>

        {backgroundImage && (
          <div className="slider-container">
            <label className="slider-label">
              Achtergrond schaal: {scale.toFixed(2)}x
            </label>
            <input
              type="range"
              min="0.1"
              max="3"
              step="0.01"
              value={scale}
              onChange={(e) => setScale(parseFloat(e.target.value))}
              className="slider"
              style={{ accentColor: '#E52F2F' }}
            />
          </div>
        )}
      </div>

      <div
        ref={canvasRef}
        className="canvas"
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        style={{
          width: '100%',
          paddingBottom: '100%', // 1:1 aspect ratio
          backgroundColor: CANVAS_COLOR,
          position: 'relative',
          maxWidth: '100%',
          maxHeight: '100vh'
        }}
      >
        <div className="canvas-content" style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0
        }}>
          {backgroundImage && (
            <img
              ref={backgroundRef}
              src={backgroundImage}
              alt="Background"
              className="background-image"
              style={{
                transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                transformOrigin: '0 0',
                ...counterStyle
              }}
            />
          )}

          {/* Fixed Logo Image using URL */}
          <div className="logo-container">
            <img
              ref={logoRef}
              src={LOGO_URL}
              alt="Logo"
              className="logo-image"
              style={{
                width: `${logoDimensions.width}px`,
                height: `${logoDimensions.height}px`
              }}
            />
          </div>

          {!backgroundImage && (
            <div className="placeholder-text">
              <p style={{ color: "#aaaaaa" }}>
                Upload een foto om te beginnen
              </p>
            </div>
          )}
        </div>
      </div>

      {backgroundImage && (
        <div className="help-text">
          <p>Sleep om de achtergrond te positioneren • Gebruik de schuifregelaar om te schalen</p>
        </div>
      )}

      <div className="top-bar">
        <div className="embed-button-container">
          <button onClick={handleCopyEmbed} className="embed-button">
            Kopieer embedcode
          </button>
          {showCopiedMessage && (
            <div className="copied-message">Copied to clipboard!</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default App;
